import { useState, useEffect, useCallback, useRef } from 'react'
import { ChevronLeft, Trash2 } from 'lucide-react'
import { useIsMobile } from '../../hooks/useIsMobile'
import {
  useUpdateNote,
  useReformatNotesJob,
  useExtractTasksJob,
  useBulkCreateTodos,
} from '../../api/hooks'
import { Button } from '../primitives/Button'
import { useToast } from '../primitives/Toast'
import { todayStr } from '../../lib/parse'
import type { ExtractedTodo, Note } from '../../api/types'
import { MarkdownEditor } from '../MarkdownEditor'
import { useDebouncedCallback } from '../../lib/useDebouncedCallback'
import { UNTITLED_NOTE_TITLE, formatNoteTime } from './noteMeta'

const NOTE_SAVE_DEBOUNCE_MS = 700

interface NoteEditorProps {
  note: Note
  /** Mobile: renders a back chevron in the header. */
  onBack?: () => void
  /** Called after the note is deleted from the editor's own delete button. */
  onDeleted?: () => void
}

/**
 * The single note editor, shared by the desktop notes page and the mobile
 * full-screen note view. Title and body autosave independently; AI reformat and
 * task extraction act on the body of whichever note is open.
 */
export function NoteEditor({ note, onBack, onDeleted }: NoteEditorProps) {
  const isMobile = useIsMobile()
  const updateNote = useUpdateNote()
  const reformatJob = useReformatNotesJob()
  const extractJob = useExtractTasksJob()
  const bulkCreate = useBulkCreateTodos()
  const { showToast } = useToast()

  const [title, setTitle] = useState(note.title)
  const [content, setContent] = useState(note.content)
  const [isDirty, setIsDirty] = useState(false)
  const [extractedTodos, setExtractedTodos] = useState<ExtractedTodo[] | null>(null)
  const [selectedTodos, setSelectedTodos] = useState<Set<number>>(new Set())
  /** Server snapshot aligned with saves; skips redundant PATCHes on duplicate onChange */
  const lastSavedRef = useRef<{ title: string; content: string }>({
    title: note.title,
    content: note.content,
  })
  const noteIdRef = useRef(note.id)
  /** Fields edited since the last save. Merged, so a title edit followed by a
   *  body edit inside one debounce window saves both. */
  const pendingPatchRef = useRef<{ title?: string; content?: string }>({})

  const debouncedSave = useDebouncedCallback(() => {
    const patch = pendingPatchRef.current
    pendingPatchRef.current = {}
    if (patch.title === undefined && patch.content === undefined) return
    updateNote.mutate(
      { id: noteIdRef.current, ...patch },
      { onError: () => showToast('Failed to save note', 'error') }
    )
  }, NOTE_SAVE_DEBOUNCE_MS)

  const queueSave = useCallback(
    (patch: { title?: string; content?: string }) => {
      pendingPatchRef.current = { ...pendingPatchRef.current, ...patch }
      debouncedSave()
    },
    [debouncedSave]
  )

  const isLocked = reformatJob.isActive || extractJob.isActive

  useEffect(() => {
    if (isLocked) debouncedSave.flush()
  }, [isLocked, debouncedSave])

  // Switching notes must not carry the previous note's unsaved edits across.
  useEffect(() => {
    if (noteIdRef.current === note.id) return
    debouncedSave.flush()
    noteIdRef.current = note.id
    setTitle(note.title)
    setContent(note.content)
    setIsDirty(false)
    setExtractedTodos(null)
    lastSavedRef.current = { title: note.title, content: note.content }
  }, [note.id, note.title, note.content, debouncedSave])

  // Sync from server — never overwrite local edits with a stale cache read.
  useEffect(() => {
    if (noteIdRef.current !== note.id) return

    if (isDirty) {
      if (note.title === title && note.content === content) {
        lastSavedRef.current = { title: note.title, content: note.content }
        setIsDirty(false)
      }
      return
    }

    lastSavedRef.current = { title: note.title, content: note.content }
    if (note.title !== title) setTitle(note.title)
    if (note.content !== content) setContent(note.content)
  }, [note, isDirty, title, content])

  // Reformat job completion
  useEffect(() => {
    const j = reformatJob.job
    if (!j || (j.status !== 'succeeded' && j.status !== 'failed' && j.status !== 'timeout')) return

    if (j.status === 'succeeded' && j.result?.content !== undefined) {
      const text = j.result.content
      setContent(text)
      setIsDirty(true)
      queueSave({ content: text })
      showToast('Note reformatted', 'success')
    } else if (j.status === 'timeout' || j.error?.code === 'timeout') {
      showToast('Operation timed out', 'error')
    } else {
      showToast('Internal error', 'error')
    }
    reformatJob.reset()
  }, [reformatJob.job, reformatJob.reset, showToast, queueSave])

  // Extract job completion
  useEffect(() => {
    const j = extractJob.job
    if (!j || (j.status !== 'succeeded' && j.status !== 'failed' && j.status !== 'timeout')) return

    if (j.status === 'succeeded' && j.result?.todos) {
      const todos = j.result.todos
      setExtractedTodos(todos)
      setSelectedTodos(new Set(todos.map((_, i) => i)))
      showToast(`Found ${todos.length} task${todos.length !== 1 ? 's' : ''}`, 'success')
    } else if (j.status === 'timeout' || j.error?.code === 'timeout') {
      showToast('Operation timed out', 'error')
    } else {
      showToast('Internal error', 'error')
    }
    extractJob.reset()
  }, [extractJob.job, extractJob.reset, showToast])

  const handleTitleChange = useCallback(
    (next: string) => {
      setTitle(next)
      setIsDirty(true)
      if (next === lastSavedRef.current.title) return
      if (!isLocked) queueSave({ title: next })
    },
    [isLocked, queueSave]
  )

  const handleEditorChange = useCallback(
    (next: string) => {
      setContent(next)
      setIsDirty(true)
      if (next === lastSavedRef.current.content) return
      if (!isLocked) queueSave({ content: next })
    },
    [isLocked, queueSave]
  )

  const handleReformat = useCallback(() => {
    debouncedSave.flush()
    reformatJob.submit({ text: content })
    showToast('Reformatting…', 'info')
  }, [debouncedSave, reformatJob, content, showToast])

  const handleExtract = useCallback(() => {
    debouncedSave.flush()
    extractJob.submit({ text: content })
    showToast('Extracting tasks…', 'info')
  }, [debouncedSave, extractJob, content, showToast])

  const handleToggleTodo = useCallback((idx: number) => {
    setSelectedTodos((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }, [])

  const handleAddToWeek = useCallback(() => {
    if (!extractedTodos) return
    const toAdd = extractedTodos.filter((_, i) => selectedTodos.has(i))
    bulkCreate.mutate(
      { todos: toAdd, defaultDate: todayStr() },
      {
        onSuccess: (created) => {
          showToast(`Added ${created.length} task${created.length !== 1 ? 's' : ''} to week`, 'success')
          setExtractedTodos(null)
          setSelectedTodos(new Set())
        },
        onError: () => showToast('Failed to add tasks', 'error'),
      }
    )
  }, [extractedTodos, selectedTodos, bulkCreate, showToast])

  let statusBanner: string | null = null
  if (reformatJob.isActive) statusBanner = 'Reformatting note…'
  else if (extractJob.isActive) statusBanner = 'Extracting tasks…'

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: isMobile ? '12px 16px 16px' : '40px 48px 32px',
        maxWidth: '880px',
        margin: '0 auto',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        {onBack && (
          <button
            onClick={onBack}
            aria-label="Back to notes"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-ink-muted)',
              padding: '6px 6px 6px 0',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ChevronLeft size={20} strokeWidth={1.5} />
          </button>
        )}
        <input
          value={title}
          onChange={(e) => handleTitleChange(e.target.value)}
          placeholder={UNTITLED_NOTE_TITLE}
          aria-label="Note title"
          className="display-serif"
          style={{
            flex: 1,
            minWidth: 0,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            padding: 0,
            color: 'var(--color-ink)',
            fontSize: isMobile ? '1.5rem' : '1.75rem',
          }}
        />
        {onDeleted && (
          <button
            onClick={() => {
              if (!window.confirm(`Delete "${title.trim() || UNTITLED_NOTE_TITLE}"?`)) return
              pendingPatchRef.current = {}
              debouncedSave.cancel()
              onDeleted()
            }}
            aria-label="Delete note"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-ink-faint)',
              padding: '6px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Trash2 size={18} strokeWidth={1.5} />
          </button>
        )}
      </div>

      {/* Meta row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
          marginBottom: '16px',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-serif)',
            fontStyle: 'italic',
            fontSize: '0.875rem',
            color: 'var(--color-ink-muted)',
          }}
        >
          {(isDirty && !isLocked) || updateNote.isPending
            ? 'saving…'
            : `edited ${formatNoteTime(note.updated_at)}`}
        </span>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="bracketed"
            onClick={handleReformat}
            loading={reformatJob.isActive}
            disabled={isLocked && !reformatJob.isActive}
          >
            reformat
          </Button>
          <Button
            variant="bracketed"
            onClick={handleExtract}
            loading={extractJob.isActive}
            disabled={isLocked && !extractJob.isActive}
          >
            extract tasks
          </Button>
        </div>
      </div>

      {statusBanner && (
        <p
          style={{
            margin: '0 0 12px',
            fontSize: '0.8125rem',
            color: 'var(--color-ink-muted)',
            fontFamily: 'var(--font-serif)',
            fontStyle: 'italic',
          }}
        >
          {statusBanner}
        </p>
      )}

      {/* Editor */}
      <div
        style={{
          position: 'relative',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          borderTop: '1px solid var(--color-border)',
          borderLeft: '1px solid var(--color-border)',
          borderTopLeftRadius: '2px',
          padding: '16px 18px 12px',
          minHeight: '260px',
          opacity: isLocked ? 0.72 : 1,
        }}
      >
        <MarkdownEditor
          value={content}
          onChange={handleEditorChange}
          readOnly={isLocked}
          placeholder="Start writing... Tasks can be extracted, prose reformatted."
          minHeight="240px"
          style={{ flex: 1, minHeight: '240px' }}
        />
      </div>

      {/* Extracted tasks preview */}
      {extractedTodos && (
        <div style={{ marginTop: '16px' }}>
          <hr className="hairline" />
          <div
            style={{
              padding: '10px 0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span className="eyebrow">
              {extractedTodos.length} task{extractedTodos.length !== 1 ? 's' : ''} found
            </span>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Button variant="ghost" size="sm" onClick={() => setExtractedTodos(null)} disabled={isLocked}>
                cancel
              </Button>
              <Button
                variant="bracketed"
                onClick={handleAddToWeek}
                disabled={selectedTodos.size === 0 || isLocked}
                loading={bulkCreate.isPending}
              >
                add {selectedTodos.size} to week
              </Button>
            </div>
          </div>
          <hr className="hairline" />

          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {extractedTodos.map((todo, idx) => (
              <label
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 0',
                  cursor: isLocked ? 'not-allowed' : 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedTodos.has(idx)}
                  onChange={() => handleToggleTodo(idx)}
                  disabled={isLocked}
                  style={{ accentColor: 'var(--color-accent)' }}
                />
                <span style={{ fontFamily: 'var(--font-serif)', fontSize: '0.875rem', color: 'var(--color-ink)' }}>
                  {todo.text}
                </span>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {todo.priority && (
                    <span
                      title={`Priority: ${todo.priority}`}
                      style={{
                        fontSize: '0.65rem',
                        fontFamily: 'var(--font-mono)',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        padding: '2px 6px',
                        borderRadius: 'var(--radius-sm)',
                        background: `var(--color-prio-${todo.priority}-bg)`,
                        color: `var(--color-prio-${todo.priority})`,
                        border: `1px solid var(--color-prio-${todo.priority})`,
                        lineHeight: 1,
                      }}
                    >
                      {todo.priority.charAt(0)}
                    </span>
                  )}
                  {todo.date && (
                    <span
                      style={{
                        fontSize: '0.75rem',
                        color: 'var(--color-ink-muted)',
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      {todo.date}
                    </span>
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
